import { z } from "zod";
import { RichText } from "../RichText";
import { ctaColorEnum, ctaClasses } from "../ctaColors";
import { globalSectionSchema, backgroundStyleEnum } from "../globalProps";
import { IMAGE_SHAPE_CLASSES } from "../imageShapes";
import { SectionShell } from "../SectionShell";

export const schema = globalSectionSchema.extend({
  heading: z.string().default("About Our Firm"),
  body: z
    .string()
    .default(
      "Founded with a commitment to client-centered service, we have spent over a decade helping individuals and businesses navigate complex challenges. Our team combines deep expertise with a genuine dedication to your success.",
    ),
  ctaLabel: z.string().optional(),
  ctaHref: z.string().optional(),
  imageUrl: z.string().optional(),
  imageOriginalUrl: z.string().optional(),
  imageOriginalStorageKey: z.string().optional(),
  imageCrop: z.object({
    x: z.number(), y: z.number(), width: z.number(), height: z.number(), zoom: z.number(),
  }).optional(),
  imagePosition: z.enum(["left", "right"]).default("left"),
  imageAlt: z.string().default(""),
  imageShape: z.enum(["square", "rounded"]).default("rounded"),
  imageAspect: z.enum(["auto", "4:3", "16:9", "1:1"]).default("auto"),
  ctaTarget: z.enum(["_self", "_blank"]).default("_self"),
  ctaColor: ctaColorEnum.optional(),
  backgroundStyle: backgroundStyleEnum.default("light"),
});

export type Props = z.infer<typeof schema>;
export const defaults: Props = schema.parse({});

const imageTextAspectClasses: Record<string, string> = {
  auto: "",
  "4:3": "aspect-[4/3]",
  "16:9": "aspect-video",
  "1:1": "aspect-square",
};

export function Component(props: Props) {
  const result = schema.safeParse(props);
  const safe = result.success ? result.data : defaults;

  const imgShape = IMAGE_SHAPE_CLASSES[safe.imageShape] || IMAGE_SHAPE_CLASSES.rounded;
  const aspectClass = imageTextAspectClasses[safe.imageAspect] || "";

  const imageBlock = (
    <div className={`${imgShape} overflow-hidden${aspectClass ? ` ${aspectClass}` : ""}`}>
      {safe.imageUrl ? (
        <img
          src={safe.imageUrl}
          alt={safe.imageAlt}
          className="w-full h-full object-cover"
          loading="lazy"
        />
      ) : (
        <div className={`${aspectClass || "aspect-[4/3]"} bg-surface ${imgShape}`} />
      )}
    </div>
  );

  const textBlock = (
    <div className="flex flex-col justify-center">
      <RichText
        as={safe.headingLevel}
        html={safe.heading}
        className="text-3xl @md:text-[2.5rem] font-bold mb-6"
        data-prop="heading"
      />
      <RichText
        as="div"
        html={safe.body}
        className="text-lg leading-relaxed opacity-90 mb-6"
        data-prop="body"
      />
      {safe.ctaLabel && safe.ctaHref && (
        <div>
          <a
            href={safe.ctaHref}
            target={safe.ctaTarget}
            rel={safe.ctaTarget === "_blank" ? "noopener noreferrer" : undefined}
            className={`inline-block font-semibold px-8 py-3 rounded-lg transition-opacity ${ctaClasses(safe.ctaColor || "primary")}`}
          >
            <RichText html={safe.ctaLabel} data-prop="ctaLabel" />
          </a>
        </div>
      )}
    </div>
  );

  return (
    <SectionShell globals={safe}>
        <div className="grid grid-cols-1 @md:grid-cols-2 gap-10 @md:gap-16 items-center">
          {safe.imagePosition === "left" ? (
            <>
              {imageBlock}
              {textBlock}
            </>
          ) : (
            <>
              {textBlock}
              {imageBlock}
            </>
          )}
        </div>
    </SectionShell>
  );
}
